<script setup lang="ts">
import { logout } from '@yc/api-client'
import { ref, watch } from 'vue'

import { useSession } from '../composables/useSession'
import AppSide from './AppSide.vue'
import TenantSwitcher from './TenantSwitcher.vue'

const props = withDefaults(
  defineProps<{
    menus: readonly import('@yc/api-client').PermNode[]
    active?: string
    appName?: string
    appColor?: string
  }>(),
  { active: '', appName: '云仓供应链平台', appColor: 'var(--yc-brand)' },
)

const emit = defineEmits<{
  (e: 'navigate', route: string): void
  (e: 'tenant-changed', tenantId: number): void
}>()

const { me, clear } = useSession()

const COLLAPSE_KEY = 'yc.sidebar-collapsed'

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    return false
  }
}

const collapsed = ref(readCollapsed())

watch(collapsed, (v) => {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0')
  } catch {
    // 忽略隐私模式下的写入失败
  }
})

function go(path: string): void {
  window.location.assign(path)
}

async function onLogout(): Promise<void> {
  try {
    await logout()
  } finally {
    clear()
    go('/login')
  }
}
</script>

<template>
  <div class="yc-shell" :class="{ 'yc-shell--collapsed': collapsed }">
    <aside class="yc-shell__side">
      <div class="yc-shell__brand">
        <span class="yc-shell__dot" :style="{ background: props.appColor }" />
        <span v-show="!collapsed" class="yc-shell__brand-name">{{ props.appName }}</span>
      </div>
      <AppSide
        :menus="props.menus"
        :active="props.active"
        :collapsed="collapsed"
        @navigate="(r) => emit('navigate', r)"
      />
    </aside>

    <div class="yc-shell__main">
      <header class="yc-shell__header">
        <el-button class="yc-shell__collapse" text size="small" @click="collapsed = !collapsed">
          {{ collapsed ? '»' : '«' }}
        </el-button>

        <div class="yc-shell__header-right">
          <TenantSwitcher @changed="(id) => emit('tenant-changed', id)" />
          <el-dropdown>
            <!-- 昵称单独一个元素承载：外层容器一旦被压窄，
                 省略号必须发生在昵称这一层而不是整个用户区，
                 否则租户标签会跟着一起被挤没。 -->
            <span class="yc-shell__user">
              <span class="yc-shell__user-name">{{ me?.name || props.appName }}</span>
              <el-tag
                v-if="me?.tenant"
                class="yc-shell__user-tag"
                size="small"
                type="info"
                effect="plain"
              >
                {{ me.tenant.name }}
              </el-tag>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="go('/profile')">个人中心</el-dropdown-item>
                <el-dropdown-item divided @click="onLogout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <main class="yc-shell__content">
        <slot />
      </main>
    </div>
  </div>
</template>

<style scoped lang="scss">
.yc-shell {
  display: grid;
  grid-template-columns: var(--yc-sidebar-width) 1fr;
  min-height: 100vh;

  &--collapsed {
    grid-template-columns: var(--yc-sidebar-width-collapsed) 1fr;
  }

  &__side {
    display: flex;
    flex-direction: column;
    background: var(--yc-card);
    border-right: 1px solid var(--yc-border);
    overflow: hidden;
  }

  &__brand {
    display: flex;
    gap: var(--yc-space-2);
    align-items: center;
    height: var(--yc-header-height);
    padding: 0 var(--yc-space-4);
    border-bottom: 1px solid var(--yc-border);
    font-weight: 600;
    white-space: nowrap;
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex: none;
  }

  &__brand-name {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__main {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__header {
    display: flex;
    gap: var(--yc-space-3);
    align-items: center;
    justify-content: space-between;
    // min-width: 0：grid 的 1fr 列默认 min-width:auto，弹性子项的最窄宽度会被
    // min-content 撑住，不写这行横向溢出会传导不回右侧、也就不会触发下面的收缩逻辑。
    min-width: 0;
    height: var(--yc-header-height);
    padding: 0 var(--yc-space-4);
    background: var(--yc-card);
    border-bottom: 1px solid var(--yc-border);
  }

  &__collapse {
    // 折叠按钮宽度固定：它是操作入口，被压扁就点不中了
    flex: none;
  }

  &__header-right {
    display: flex;
    gap: var(--yc-space-4);
    align-items: center;
    // 允许这一整块被压窄。压窄量只会流向昵称（唯一可压缩的子项），
    // 租户选择器是 flex:none，见 TenantSwitcher。
    min-width: 0;
  }

  &__user {
    display: inline-flex;
    gap: var(--yc-space-2);
    align-items: center;
    // 顶栏右侧被挤扁的真实原因：中文可以在任意两个字符之间断行，
    // 这个 inline-flex 容器的 min-content 宽度就是「一个字」，
    // 一旦 header 宽度不够，浏览器就会把它一路压到 min-content，
    // 「平台管理员」于是竖排成一行一个字（线上 yc.sasago.com 截图）。
    // 下面两条是把它从 min-content 里救出来：nowrap 不许断行，
    // max-width 给一个上限（超出部分交给昵称自己省略），20em ≈ 280px，
    // 在 1024px 视口下顶栏右侧仍放得下（实测余量 200px），不会反过来把租户选择器顶出去。
    white-space: nowrap;
    max-width: 20em;
    min-width: 0;
    cursor: pointer;
  }

  &__user-name {
    // flex 子项默认 min-width:auto，等于不允许比 min-content 更窄，
    // 省略号永远不会被触发；必须显式给 min-width:0。
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__user-tag {
    // 租户标签是身份标识，优先保住它，压缩全由昵称承担
    flex: none;
    max-width: 12em;

    :deep(.el-tag__content) {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    padding: var(--yc-space-6);
  }
}
</style>